/**
 * Centralized Unicode Input Validation & Sanitization Utility
 * 
 * Target Blocked Unicode Ranges:
 * 1. U+0080 - U+07FF (2 UTF-8 bytes): Latin extensions, Greek, Cyrillic, Hebrew, Arabic, etc.
 * 2. U+0800 - U+D7FF (3 UTF-8 bytes): Asian scripts (Chinese, Japanese, Korean), Indic scripts (Hindi, Bengali, etc.), symbols, punctuation.
 * 
 * Standard ASCII (U+0000 - U+007F, e.g. English letters A-Z/a-z, numbers 0-9, standard symbols & spaces) are ALLOWED.
 */

// Regular expression matching characters in the blocked ranges: U+0080 - U+07FF and U+0800 - U+D7FF
export const BLOCKED_UNICODE_REGEX = /[\u0080-\uD7FF]/g;

/**
 * Checks if a string contains any blocked Unicode characters.
 * @param {string} value - The input text to check.
 * @returns {boolean} - True if any blocked character is found, false otherwise.
 */
export const hasBlockedUnicode = (value) => {
  if (typeof value !== "string" || !value) return false;
  return /[\u0080-\uD7FF]/.test(value);
};

/**
 * Strips/removes all blocked Unicode characters from a string.
 * @param {string} value - The input text to sanitize.
 * @returns {string} - The sanitized text containing only allowed characters.
 */
export const sanitizeBlockedUnicode = (value) => {
  if (typeof value !== "string" || !value) return value || "";
  return value.replace(BLOCKED_UNICODE_REGEX, "");
};

/**
 * Validates a value against the blocked Unicode ranges.
 * @param {string} value - The input value to validate.
 * @returns {{ isValid: boolean, sanitized: string, hasBlockedChars: boolean }}
 */
export const validateBlockedUnicode = (value) => {
  const hasBlocked = hasBlockedUnicode(value);
  return {
    isValid: !hasBlocked,
    sanitized: sanitizeBlockedUnicode(value),
    hasBlockedChars: hasBlocked,
  };
};

/**
 * KeyDown / KeyPress handler that prevents typing characters in the blocked Unicode ranges.
 * @param {KeyboardEvent} e - React or Native KeyboardEvent.
 */
export const handleBlockedUnicodeKeyDown = (e) => {
  // Allow navigation, control keys, backspace, delete, tab, enter, arrow keys, copy/paste shortcuts
  if (
    !e.key ||
    e.key.length > 1 ||
    e.ctrlKey ||
    e.metaKey ||
    e.altKey
  ) {
    return;
  }

  if (hasBlockedUnicode(e.key)) {
    e.preventDefault();
  }
};

/**
 * Paste event handler that filters out blocked Unicode characters upon paste.
 * @param {ClipboardEvent} e - React or Native ClipboardEvent.
 * @param {Function} [onValueChange] - Optional callback receiving the sanitized paste or full input value.
 */
export const handleBlockedUnicodePaste = (e, onValueChange) => {
  const clipboardData = e.clipboardData || window.clipboardData;
  if (!clipboardData) return;

  const pastedText = clipboardData.getData("text");
  if (!hasBlockedUnicode(pastedText)) return;

  e.preventDefault();
  const sanitizedText = sanitizeBlockedUnicode(pastedText);

  if (typeof onValueChange === "function") {
    onValueChange(sanitizedText);
  } else if (e.target && typeof e.target.value === "string") {
    const start = e.target.selectionStart || 0;
    const end = e.target.selectionEnd || 0;
    const original = e.target.value;
    const updated =
      original.substring(0, start) + sanitizedText + original.substring(end);
    e.target.value = updated;

    // Trigger synthetic input/change event
    const event = new Event("input", { bubbles: true });
    e.target.dispatchEvent(event);
  }
};

/**
 * onChange / onInput interceptor helper.
 * Automatically removes any blocked characters from e.target.value and invokes your original onChange handler.
 * 
 * Usage:
 * <input onChange={filterBlockedUnicodeChange((e) => this.setState({ name: e.target.value }))} />
 * 
 * @param {Function} originalOnChange - Your component's onChange callback.
 * @returns {Function} - Wrapped onChange event handler.
 */
export const filterBlockedUnicodeChange = (originalOnChange) => {
  return (e) => {
    if (e && e.target && typeof e.target.value === "string") {
      const originalValue = e.target.value;
      if (hasBlockedUnicode(originalValue)) {
        e.target.value = sanitizeBlockedUnicode(originalValue);
      }
    }
    if (typeof originalOnChange === "function") {
      originalOnChange(e);
    }
  };
};

/**
 * Generates standard input props to easily attach to any <input> or <textarea>.
 * 
 * Usage:
 * <input {...getBlockedUnicodeInputProps()} onChange={(e) => ...} />
 */
export const getBlockedUnicodeInputProps = (customOnChange) => ({
  onKeyDown: handleBlockedUnicodeKeyDown,
  onPaste: (e) => handleBlockedUnicodePaste(e),
  onChange: customOnChange ? filterBlockedUnicodeChange(customOnChange) : undefined,
});

export default {
  BLOCKED_UNICODE_REGEX,
  hasBlockedUnicode,
  sanitizeBlockedUnicode,
  validateBlockedUnicode,
  handleBlockedUnicodeKeyDown,
  handleBlockedUnicodePaste,
  filterBlockedUnicodeChange,
  getBlockedUnicodeInputProps,
};
